import React from 'react';
import { RichTextEditor, Link, useRichTextEditorContext } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { Document } from '@tiptap/extension-document';
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Strike } from "@tiptap/extension-strike";

import { IconMoodSmile, IconCode, IconCheck, IconLetterCaseUpper, IconLetterCaseLower, IconLetterCase } from '@tabler/icons-react';
import { Group, Input, Menu, Modal, Popover, Text, Textarea, useComputedColorScheme, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import CharacterCount from '@tiptap/extension-character-count';

import '@mantine/tiptap/styles.css';
import classes from './RichTextEditorComponent.module.css';
import { useTranslation } from "react-i18next";

const InsertEmojiControl = () => {
  const { t } = useTranslation();
  const { editor } = useRichTextEditorContext();

  return (
    <Popover width={350} position="bottom" withArrow shadow="md">
      <Popover.Target>
        <RichTextEditor.Control aria-label={t('editor.selectEmoji')} title={t('editor.selectEmoji')}>
          <IconMoodSmile stroke={1.5} size={16} />
        </RichTextEditor.Control>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Picker data={data} onEmojiSelect={({ native }) => editor?.commands.insertContent(native)} />
      </Popover.Dropdown>
    </Popover>
  )
}

const CodeEditorControl = () => {
  const { t } = useTranslation();
  const { editor } = useRichTextEditorContext();
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <Modal opened={opened} onClose={close} title={t('editor.sourceEditor')} centered size="xl" radius="md">
        <Textarea
          radius="md"
          defaultValue={editor?.getHTML()}
          minRows={12}
          autosize
          onChange={(event) => {
            editor?.commands.setContent(event.currentTarget.value)
            editor?.view.dispatch(editor.state.tr);
          }}
        />
      </Modal>
      <RichTextEditor.Control onClick={open}>
        <IconCode stroke={1.5} size={16} />
      </RichTextEditor.Control>
    </>
  )
}

/** ---------- Case helpers ---------- */

const toSentenceCase = (str = '') => {
  const lowered = str.toLowerCase();
  return lowered
    .replace(/(^\s*[a-zа-я])/giu, (m) => m.toUpperCase()) // start
    .replace(/([.!?]\s+)([a-zа-я])/giu, (_, p1, p2) => p1 + p2.toUpperCase()); // after punctuation
};

const toUpperCase = (str = '') => str.toUpperCase();
const toLowerCase = (str = '') => str.toLowerCase();

const transformAllTextNodes = (editor, transformFn) => {
  if (!editor) return;
  const { state, view } = editor;
  let tr = state.tr;

  state.doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      const nextText = transformFn(node.text);
      if (nextText !== node.text) {
        tr = tr.insertText(nextText, pos, pos + node.text.length);
      }
    }
  });

  if (tr.docChanged) {
    view.dispatch(tr);
    editor.chain().focus().run();
  }
};

const transformSelectionText = (editor, transformFn) => {
  if (!editor) return;
  const { from, to } = editor.state.selection;

  if (from === to) {
    // No selection: transform entire document text nodes (preserves marks)
    transformAllTextNodes(editor, transformFn);
    return;
  }

  const selected = editor.state.doc.textBetween(from, to, '\n');
  const transformed = transformFn(selected);

  editor
    .chain()
    .focus()
    .command(({ tr }) => {
      tr.insertText(transformed, from, to);
      return true;
    })
    .run();
};

const CaseControls = ({ editor }) => {
  const { t } = useTranslation();
  if (!editor) return null;

  return (
    <RichTextEditor.ControlsGroup>
      <Tooltip label={t('editor.sentenceCase')} withArrow>
        <RichTextEditor.Control
          aria-label={t('editor.sentenceCase')}
          title={t('editor.sentenceCase')}
          onClick={() => transformSelectionText(editor, toSentenceCase)}
        >
          <IconLetterCase size={16} />
        </RichTextEditor.Control>
      </Tooltip>

      <Tooltip label="UPPERCASE" withArrow>
        <RichTextEditor.Control
          aria-label="UPPERCASE"
          title="UPPERCASE"
          onClick={() => transformSelectionText(editor, toUpperCase)}
        >
          <IconLetterCaseUpper size={16} />
        </RichTextEditor.Control>
      </Tooltip>

      <Tooltip label={t('editor.lowercase')} withArrow>
        <RichTextEditor.Control
          aria-label={t('editor.lowercase')}
          title={t('editor.lowercase')}
          onClick={() => transformSelectionText(editor, toLowerCase)}
        >
          <IconLetterCaseLower size={16} />
        </RichTextEditor.Control>
      </Tooltip>
    </RichTextEditor.ControlsGroup>
  );
};

export const RichTextEditorComponent = ({ withCharacterCount = true, maxChar = 35, hasLink = true, hasCodeView = true, showCaseControls = false, ...props }) => {
  const { t } = useTranslation();
  const computedColorScheme = useComputedColorScheme('light');

  const CustomDocument = Document.extend({
    ...(props?.hasHeading || props?.hasOrderedList || props?.hasBulletList || props?.hasTextAlign
      ? {}
      : { content: 'inline*' }),
    addKeyboardShortcuts() {
      const useEnter = (props?.hasHeading || props?.hasOrderedList || props?.hasBulletList || props?.hasTextAlign) || false;
      return !useEnter
        ? {
            Enter: () => this.editor.chain().focus().setHardBreak().run(),
          }
        : {
            'Shift-Enter': () => this.editor.chain().focus().setHardBreak().run(),
          };
    },
  });

  const CustomBold = Bold.extend({
    renderHTML({ mark, HTMLAttributes }) {
      return ["b", HTMLAttributes, 0];
    },
    parseHTML() {
      return [{ tag: "b" }];
    },
  });

  const CustomItalic = Italic.extend({
    renderHTML({ mark, HTMLAttributes }) {
      return ["i", HTMLAttributes, 0];
    },
    parseHTML() {
      return [{ tag: "i" }];
    },
  });

  const CustomStrikethrough = Strike.extend({
    renderHTML({ mark, HTMLAttributes }) {
      return ["strike", HTMLAttributes, 0];
    },
    parseHTML() {
      return [{ tag: "strike" }];
    },
  });

  const TextFormatControl = () => {
    const { editor } = useRichTextEditorContext();

    if (!editor) return null;

    const setHeading = (level) => {
      editor.chain().focus().toggleHeading({ level }).run();
    };

    const headingLevels = [1, 2, 3, 4];

    return (
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <RichTextEditor.Control px={6} gap={4}>
            <IconLetterCase size={16} /> <Text ml={4} fz="xs">{t('editor.textFormat')}</Text>
          </RichTextEditor.Control>
        </Menu.Target>
        <Menu.Dropdown>
          {headingLevels.map((level) => (
            <Menu.Item
              key={level}
              onClick={() => setHeading(level)}
              rightSection={editor.isActive('heading', { level }) ? (<IconCheck size={16} stroke={2} />) : null}
            >
              {React.createElement(`h${level}`, { style: { margin: 0 } }, `Heading ${level}`)}
            </Menu.Item>
          ))}
          <Menu.Item
            onClick={() => editor.chain().focus().setParagraph().resetAttributes('paragraph').run()}
            rightSection={editor.isActive('paragraph') && !editor.getAttributes('paragraph')?.class ? (<IconCheck size={14} stroke={2} />) : null}
          >
            {t('editor.normal')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        ...((props?.hasHeading || props?.hasOrderedList || props?.hasBulletList || props?.hasTextAlign) ? {} : { document: false }),
        bold: false,
        italic: false,
        strike: false,
      }),
      CustomDocument,
      CustomBold,
      CustomItalic,
      CustomStrikethrough,
      Underline,
      Link,
      ...(props?.hasTextAlign ? [TextAlign.configure({ types: ['heading', 'paragraph'] })] : []),
      Placeholder.configure({ placeholder: props?.placeholder || '' }),
      CharacterCount.configure({ mode: 'textSize' }),
    ],
    content: props?.defaultValue,
    onUpdate: ({ editor }) => {
      let rawHTML = editor.getHTML();

      // strip empty tags
      while (/<([a-z][a-z0-9]*)\b[^>]*>\s*<\/\1>/gi.test(rawHTML)) {
        rawHTML = rawHTML.replace(/<([a-z][a-z0-9]*)\b[^>]*>\s*<\/\1>/gi, '');
      }

      // Alignment lives on the paragraph, so it cannot survive the strip the ad templates need.
      const cleanedHTML = props?.hasTextAlign ? rawHTML : rawHTML.replace(/<\/?p>/g, '');

      if (props?.isDirty) {
        let finalHTML = cleanedHTML.trim();

        if (!/^<p[\s>]/i.test(finalHTML) || !/<\/p>$/i.test(finalHTML)) {
          finalHTML = `<p>${finalHTML}</p>`;
        }

        props?.onChange(finalHTML);
        return;
      }

      props?.onChange(cleanedHTML);
    },
  });

  const getColor = () => {
    const percentage = (editor.storage.characterCount.characters() / maxChar) * 100;

    if (percentage < 57 || percentage >= 142) return "red";
    if (percentage < 80) return "orange";
    if (percentage <= 100) return "green";
    return "orange"; // Beyond max limit
  };

  const toolbarBgColor = computedColorScheme === 'light' ? 'gray.3' : 'dark.8';
  const contentBgColor = computedColorScheme === 'light' ? 'gray.0' : 'dark.4';

  return (
    <Input.Wrapper className="wrapper-label" label={props?.label || ''} description={props?.description || ''} error={props?.error || ''}>
      <RichTextEditor editor={editor} classNames={classes}>
        <RichTextEditor.Toolbar style={{ justifyContent: "space-between" }} bg={toolbarBgColor}>
          <Group gap="md">
            <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
            </RichTextEditor.ControlsGroup>

            {props?.hasHeading && (
              <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
                <RichTextEditor.H1 />
                <RichTextEditor.H2 />
                <RichTextEditor.H3 />
                <RichTextEditor.H4 />
              </RichTextEditor.ControlsGroup>
            )}

            <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
              {props?.hasBulletList && (<RichTextEditor.BulletList />)}
              {props?.hasOrderedList && (<RichTextEditor.OrderedList />)}
            </RichTextEditor.ControlsGroup>

            {props?.hasTextAlign && (
              <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
                <RichTextEditor.AlignLeft />
                <RichTextEditor.AlignCenter />
                <RichTextEditor.AlignRight />
                <RichTextEditor.AlignJustify />
              </RichTextEditor.ControlsGroup>
            )}

            <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
              {hasLink && (
                <>
                  <RichTextEditor.Link />
                  <RichTextEditor.Unlink />
                </>
              )}
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
              <InsertEmojiControl />
            </RichTextEditor.ControlsGroup>

            {/* Case controls for Slogan when enabled */}
            {showCaseControls && (
              <CaseControls editor={editor} />
            )}

            <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
              <RichTextEditor.Undo />
              <RichTextEditor.Redo />
              {hasCodeView && (<CodeEditorControl />)}
            </RichTextEditor.ControlsGroup>
          </Group>

          <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
            {withCharacterCount && (
              <Text size="sm" fw="bold" c={getColor()}>
                {editor.storage.characterCount.characters()} / {maxChar}
              </Text>
            )}
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>

        <RichTextEditor.Content mih={80} bg={contentBgColor} {...props?.contentProps} />
      </RichTextEditor>
    </Input.Wrapper>
  );
}
